import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { supabase } from '../../../lib/supabase';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

const ABUSE_TYPES = ['off_platform_contact', 'abuse_detected', 'unusual_usage', 'suspicious_signup'];

const AbuseMonitoringPanel = () => {
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [severityFilter, setSeverityFilter] = useState('all');
  const [actionLoading, setActionLoading] = useState(null);

  useEffect(() => {
    loadAlerts();
  }, []);

  const loadAlerts = async () => {
    try {
      setLoading(true);

      const { data: alertsData, error } = await supabase
        ?.from('admin_alerts')
        ?.select('*')
        ?.in('alert_type', ABUSE_TYPES)
        ?.order('created_at', { ascending: false });

      if (error) throw error;

      // Enrich with user profiles
      const userIds = [...new Set((alertsData || [])?.map(a => a?.user_id)?.filter(Boolean))];
      let userMap = {};
      if (userIds?.length > 0) {
        const { data: profiles } = await supabase
          ?.from('user_profiles')
          ?.select('id, email, full_name, is_suspended')
          ?.in('id', userIds);
        userMap = (profiles || [])?.reduce((acc, p) => { acc[p?.id] = p; return acc; }, {});
      }

      const enriched = (alertsData || [])?.map(a => ({
        ...a,
        userProfile: userMap[a?.user_id] || null,
        displayStatus: a?.resolved ? 'resolved' : a?.acknowledged ? 'investigating' : 'pending',
      }));

      setAlerts(enriched);
    } catch (error) {
      console.error('Load abuse alerts error:', error);
      toast?.error('Failed to load abuse monitoring data');
    } finally {
      setLoading(false);
    }
  };

  const handleInvestigate = (alert) => {
    // Navigate to the place where the incident occurred based on alert type and available IDs
    const type = alert?.alert_type;
    const messageId = alert?.message_id;
    const contractId = alert?.contract_id;
    const userId = alert?.user_id;
    const caseId = alert?.metadata?.caseId || alert?.case_id;

    if ((type === 'off_platform_contact' || type === 'abuse_detected') && messageId) {
      // Go directly to flagged messages tab filtered to this message
      navigate(`/admin-dashboard?tab=flagged-messages&messageId=${messageId}`);
    } else if ((type === 'off_platform_contact' || type === 'abuse_detected') && contractId) {
      navigate(`/admin-dashboard?tab=contracts&contractId=${contractId}`);
    } else if (type === 'unusual_usage' && caseId) {
      navigate(`/report-viewer/${caseId}`);
    } else if ((type === 'suspicious_signup' || type === 'unusual_usage') && userId) {
      navigate(`/admin-dashboard?tab=users&userId=${userId}`);
    } else if (contractId) {
      navigate(`/admin-dashboard?tab=contracts&contractId=${contractId}`);
    } else if (caseId) {
      navigate(`/report-viewer/${caseId}`);
    } else {
      // Fallback: open flagged messages tab
      navigate('/admin-dashboard?tab=flagged-messages');
    }
  };

  const handleResolve = async (alert) => {
    try {
      setActionLoading(alert?.id);
      const { data: { user } } = await supabase?.auth?.getUser();
      const { error } = await supabase
        ?.from('admin_alerts')
        ?.update({
          resolved: true,
          resolved_by: user?.id,
          resolved_at: new Date()?.toISOString(),
          acknowledged: true,
          acknowledged_by: user?.id,
          acknowledged_at: new Date()?.toISOString(),
        })
        ?.eq('id', alert?.id);
      if (error) throw error;
      toast?.success('Alert resolved');
      await loadAlerts();
    } catch (error) {
      console.error('Resolve error:', error);
      toast?.error('Failed to resolve alert');
    } finally {
      setActionLoading(null);
    }
  };

  const handleSuspendUser = async (alert) => {
    if (!alert?.user_id) {
      toast?.error('No user associated with this alert');
      return;
    }
    try {
      setActionLoading(alert?.id);
      const { data: { user } } = await supabase?.auth?.getUser();
      const { error } = await supabase
        ?.from('user_profiles')
        ?.update({
          is_suspended: true,
          suspended_at: new Date()?.toISOString(),
          suspended_by: user?.id,
          suspension_reason: `Suspended via abuse alert: ${alert?.title}`,
        })
        ?.eq('id', alert?.user_id);
      if (error) throw error;
      toast?.success(`User ${alert?.userProfile?.email || ''} suspended`);
      await loadAlerts();
    } catch (error) {
      console.error('Suspend user error:', error);
      toast?.error('Failed to suspend user');
    } finally {
      setActionLoading(null);
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return 'bg-error/10 text-error border-error/20';
      case 'high': return 'bg-error/10 text-error border-error/20';
      case 'medium': return 'bg-warning/10 text-warning border-warning/20';
      case 'low': return 'bg-muted text-muted-foreground border-border';
      default: return 'bg-muted text-muted-foreground border-border';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-warning/10 text-warning';
      case 'investigating': return 'bg-primary/10 text-primary';
      case 'resolved': return 'bg-success/10 text-success';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'off_platform_contact': return 'AlertTriangle';
      case 'abuse_detected': return 'ShieldAlert';
      case 'unusual_usage': return 'Activity';
      case 'suspicious_signup': return 'UserX';
      default: return 'AlertCircle';
    }
  };

  const getTypeLabel = (type) => {
    const labels = {
      off_platform_contact: 'Off-Platform Contact',
      abuse_detected: 'Abuse Detected',
      unusual_usage: 'Unusual Usage',
      suspicious_signup: 'Suspicious Signup',
    };
    return labels[type] || type;
  };

  const filteredAlerts = alerts?.filter(
    a => severityFilter === 'all' || a?.severity === severityFilter
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h3 className="text-lg font-semibold text-foreground">Abuse Monitoring</h3>
          <p className="text-sm text-muted-foreground">
            {filteredAlerts?.length} alert{filteredAlerts?.length !== 1 ? 's' : ''} detected
          </p>
        </div>
        <select
          value={severityFilter}
          onChange={e => setSeverityFilter(e?.target?.value)}
          className="px-3 py-2 bg-background border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary w-full sm:w-48"
        >
          <option value="all">All Severity</option>
          <option value="critical">Critical</option>
          <option value="high">High</option>
          <option value="medium">Medium</option>
          <option value="low">Low</option>
        </select>
      </div>

      {filteredAlerts?.length === 0 ? (
        <div className="text-center py-12 bg-card border border-border rounded-lg">
          <Icon name="ShieldCheck" size={48} className="mx-auto mb-4 text-success" />
          <p className="text-muted-foreground">No abuse alerts to display</p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredAlerts?.map((alert) => (
            <div
              key={alert?.id}
              className="bg-card border border-border rounded-lg p-4 md:p-6 hover:shadow-elevation-md transition-smooth"
            >
              {/* Title row */}
              <div className="flex items-start gap-3 mb-4">
                <div className={`flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center border ${getSeverityColor(alert?.severity)}`}>
                  <Icon name={getTypeIcon(alert?.alert_type)} size={20} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <h4 className="font-semibold text-foreground">{alert?.title}</h4>
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${getSeverityColor(alert?.severity)}`}>
                      {alert?.severity?.toUpperCase()}
                    </span>
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${getStatusColor(alert?.displayStatus)}`}>
                      {alert?.displayStatus?.charAt(0)?.toUpperCase() + alert?.displayStatus?.slice(1)}
                    </span>
                  </div>
                  {alert?.description && (
                    <p className="text-sm text-muted-foreground">{alert?.description}</p>
                  )}
                  <p className="text-xs text-muted-foreground mt-1">
                    {new Date(alert?.created_at)?.toLocaleString('en-US', {
                      month: 'short', day: 'numeric', year: 'numeric',
                      hour: 'numeric', minute: '2-digit'
                    })}
                  </p>
                </div>
              </div>

              {/* Details grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4 p-4 bg-muted/40 rounded-lg text-sm">
                <div>
                  <p className="text-xs text-muted-foreground mb-0.5">Alert Type</p>
                  <p className="text-foreground font-medium">{getTypeLabel(alert?.alert_type)}</p>
                </div>
                {alert?.userProfile && (
                  <div>
                    <p className="text-xs text-muted-foreground mb-0.5">User</p>
                    <p className="text-foreground font-medium">{alert?.userProfile?.full_name || '—'}</p>
                    <p className="text-xs text-muted-foreground">{alert?.userProfile?.email}</p>
                    {alert?.userProfile?.is_suspended && (
                      <span className="inline-block mt-1 px-2 py-0.5 bg-error/10 text-error text-xs rounded font-medium">
                        Suspended
                      </span>
                    )}
                  </div>
                )}
                {alert?.metadata?.caseId && (
                  <div>
                    <p className="text-xs text-muted-foreground mb-0.5">Case ID</p>
                    <p className="text-foreground font-mono text-sm">{alert?.metadata?.caseId}</p>
                  </div>
                )}
                {alert?.metadata?.evidence && (
                  <div className="md:col-span-2">
                    <p className="text-xs text-muted-foreground mb-0.5">Evidence</p>
                    <p className="text-foreground font-mono text-sm bg-error/5 border border-error/20 p-2 rounded">
                      {alert?.metadata?.evidence}
                    </p>
                  </div>
                )}
              </div>

              {/* Action buttons */}
              <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-border">
                {alert?.displayStatus === 'pending' && (
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      iconName="Search"
                      onClick={() => handleInvestigate(alert)}
                      disabled={actionLoading === alert?.id}
                    >
                      Investigate
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      iconName="CheckCircle"
                      onClick={() => handleResolve(alert)}
                      disabled={actionLoading === alert?.id}
                    >
                      Resolve
                    </Button>
                    {alert?.user_id && !alert?.userProfile?.is_suspended && (
                      <Button
                        variant="danger"
                        size="sm"
                        iconName="Ban"
                        onClick={() => handleSuspendUser(alert)}
                        disabled={actionLoading === alert?.id}
                      >
                        Suspend User
                      </Button>
                    )}
                  </>
                )}
                {alert?.displayStatus === 'investigating' && (
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      iconName="CheckCircle"
                      onClick={() => handleResolve(alert)}
                      disabled={actionLoading === alert?.id}
                    >
                      Mark Resolved
                    </Button>
                    {alert?.user_id && !alert?.userProfile?.is_suspended && (
                      <Button
                        variant="danger"
                        size="sm"
                        iconName="Ban"
                        onClick={() => handleSuspendUser(alert)}
                        disabled={actionLoading === alert?.id}
                      >
                        Suspend User
                      </Button>
                    )}
                  </>
                )}
                {alert?.displayStatus === 'resolved' && (
                  <span className="flex items-center gap-1.5 text-sm text-success">
                    <Icon name="CheckCircle" size={16} />
                    Resolved{alert?.resolved_at ? ` · ${new Date(alert?.resolved_at)?.toLocaleDateString()}` : ''}
                  </span>
                )}
                {actionLoading === alert?.id && (
                  <span className="flex items-center gap-1.5 text-sm text-muted-foreground ml-1">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary" />
                    Processing…
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AbuseMonitoringPanel;
